/**
 * Панель фильтров для таблиц аккаунтов: поиск + мультиселекты (статусы,
 * модели, телефоны, типы — можно выбрать несколько значений сразу) + булевы
 * (варн/оформление/залито). Используется на страницах «Все аккаунты»,
 * «Телефон», «Модель» (лишние селекты скрываются пропсами).
 */
import { useEffect, useRef, useState } from 'react';
import type { Account, ModelSummary, PhoneSummary } from '@wml-insta/shared';
import { ACCOUNT_STATUSES } from '@wml-insta/shared';
import type { AccountStatus } from '@wml-insta/shared';

export interface AccountFiltersValue {
  q: string;
  statuses: string[];     // статусы + 'none'
  modelIds: string[];     // id моделей + 'none'
  phoneIds: string[];
  accountTypes: string[]; // названия типов + 'none'
  startDates: string[];   // даты старта YYYY-MM-DD + 'none' (пачки акков)
  warn: string;           // '' | '1' | '0'
  decorated: string;
  poured: string;
  /** Только «необработавшие» — автопостер не выложил план (отработка не N/N). */
  unworked: boolean;
}

export const EMPTY_FILTERS: AccountFiltersValue = {
  q: '', statuses: [], modelIds: [], phoneIds: [], accountTypes: [], startDates: [],
  warn: '', decorated: '', poured: '', unworked: false,
};

export function filterAccounts(accounts: Account[], f: AccountFiltersValue): Account[] {
  const needle = f.q.trim().toLowerCase();
  return accounts.filter((a) => {
    if (needle && !(`${a.username} ${a.creoName ?? ''} ${a.batch ?? ''} ${a.note ?? ''}`.toLowerCase().includes(needle))) return false;
    if (f.statuses.length && !f.statuses.includes(a.status ?? 'none')) return false;
    if (f.modelIds.length && !f.modelIds.includes(a.modelId ?? 'none')) return false;
    if (f.phoneIds.length && !f.phoneIds.includes(a.phoneId)) return false;
    if (f.accountTypes.length && !f.accountTypes.includes(a.accountType ?? 'none')) return false;
    if (f.startDates.length && !f.startDates.includes(a.startDate ?? 'none')) return false;
    if (f.warn && a.warn !== (f.warn === '1')) return false;
    if (f.decorated && a.decorated !== (f.decorated === '1')) return false;
    if (f.poured && a.poured !== (f.poured === '1')) return false;
    if (f.unworked && !a.unworked) return false;
    return true;
  });
}

// ---------- мультиселект с поповером ----------

interface Option { value: string; label: string }

const POPOVER_W = 240;

function MultiFilter({ label, options, selected, onChange }: {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  function openPopover() {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 6;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - POPOVER_W - 8));
    setPos({ top, left, maxHeight: Math.max(160, window.innerHeight - top - 16) });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Прокрутка внутри поповера не должна его закрывать (см. ColumnsToggle).
    const onScrollResize = (e?: Event) => {
      if (e && e.target instanceof Node && wrapRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollResize);
    window.addEventListener('scroll', onScrollResize, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollResize);
      window.removeEventListener('scroll', onScrollResize, true);
    };
  }, [open]);

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  const active = selected.length > 0;
  const summary = active
    ? selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label ?? label
      : `${label}: ${selected.length}`
    : label;

  return (
    <div className="columns-wrap" ref={wrapRef}>
      <button
        ref={btnRef}
        className={`filter-multi-btn ${active ? 'active' : ''}`}
        onClick={() => (open ? setOpen(false) : openPopover())}
        title={label}
      >
        <span className="fm-label">{summary}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && pos && (
        <div
          className="columns-popover"
          style={{ position: 'fixed', top: pos.top, left: pos.left, right: 'auto', width: POPOVER_W, maxHeight: pos.maxHeight }}
        >
          {options.map((o) => (
            <button key={o.value} className="col-item" onClick={() => toggle(o.value)}>
              <input type="checkbox" checked={selected.includes(o.value)} readOnly tabIndex={-1} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
            </button>
          ))}
          {active && (
            <div className="col-actions">
              <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 8px' }} onClick={() => { onChange([]); setOpen(false); }}>
                Сбросить
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- сама панель ----------

interface Props {
  value: AccountFiltersValue;
  onChange: (next: AccountFiltersValue) => void;
  accounts: Account[];
  models?: ModelSummary[];
  phones?: PhoneSummary[];
  showModel?: boolean;
  showPhone?: boolean;
  /** Какие статусы предлагать в фильтре (рабочие экраны — активные, архив — архивные). */
  statusOptions?: readonly AccountStatus[];
  /** Показывать пункт «без статуса». */
  allowNoStatus?: boolean;
}

export function AccountFilters({
  value, onChange, accounts, models = [], phones = [], showModel = true, showPhone = true,
  statusOptions = ACCOUNT_STATUSES, allowNoStatus = true,
}: Props) {
  const set = (patch: Partial<AccountFiltersValue>) => onChange({ ...value, ...patch });
  const types = Array.from(new Set(accounts.map((a) => a.accountType).filter((t): t is string => Boolean(t)))).sort();
  // Даты старта, доступные у отсматриваемого набора акков — от свежих к старым.
  const startDates = Array.from(new Set(accounts.map((a) => a.startDate).filter((d): d is string => Boolean(d))))
    .sort((a, b) => b.localeCompare(a));
  const hasNoStartDate = accounts.some((a) => !a.startDate);
  const hasActive =
    value.q.trim() !== '' || value.statuses.length > 0 || value.modelIds.length > 0 ||
    value.phoneIds.length > 0 || value.accountTypes.length > 0 || value.startDates.length > 0 ||
    value.warn !== '' || value.decorated !== '' || value.poured !== '' || value.unworked;

  return (
    <>
      <input
        className="admin-input"
        placeholder="Поиск: юзернейм, крео, пачка…"
        value={value.q}
        onChange={(e) => set({ q: e.target.value })}
        style={{ width: 220 }}
      />
      <MultiFilter
        label="Все статусы"
        options={[
          ...statusOptions.map((s) => ({ value: s, label: s })),
          ...(allowNoStatus ? [{ value: 'none', label: '— без статуса —' }] : []),
        ]}
        selected={value.statuses}
        onChange={(statuses) => set({ statuses })}
      />
      {showModel && (
        <MultiFilter
          label="Все модели"
          options={[
            ...models.map((m) => ({ value: m.id, label: m.name })),
            { value: 'none', label: '— без модели —' },
          ]}
          selected={value.modelIds}
          onChange={(modelIds) => set({ modelIds })}
        />
      )}
      {showPhone && (
        <MultiFilter
          label="Все телефоны"
          options={phones.map((p) => ({ value: p.id, label: p.name }))}
          selected={value.phoneIds}
          onChange={(phoneIds) => set({ phoneIds })}
        />
      )}
      <MultiFilter
        label="Все типы"
        options={[
          ...types.map((t) => ({ value: t, label: t })),
          { value: 'none', label: '— без типа —' },
        ]}
        selected={value.accountTypes}
        onChange={(accountTypes) => set({ accountTypes })}
      />
      <MultiFilter
        label="Дата старта"
        options={[
          ...startDates.map((d) => ({
            value: d,
            label: new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' }),
          })),
          ...(hasNoStartDate ? [{ value: 'none', label: '— без даты —' }] : []),
        ]}
        selected={value.startDates}
        onChange={(sd) => set({ startDates: sd })}
      />
      <select className="admin-input filter-fixed" value={value.warn} onChange={(e) => set({ warn: e.target.value })} title="Варн">
        <option value="">Варн: все</option>
        <option value="1">Варн: да</option>
        <option value="0">Варн: нет</option>
      </select>
      <select className="admin-input filter-fixed" value={value.poured} onChange={(e) => set({ poured: e.target.value })} title="Залито">
        <option value="">Залито: все</option>
        <option value="1">Залито: да</option>
        <option value="0">Залито: нет</option>
      </select>
      <select className="admin-input filter-fixed" value={value.decorated} onChange={(e) => set({ decorated: e.target.value })} title="Оформление">
        <option value="">Оформл.: все</option>
        <option value="1">Оформл.: да</option>
        <option value="0">Оформл.: нет</option>
      </select>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }} title="Автопостер не выложил план — отработка не N/N">
        <input
          type="checkbox"
          checked={value.unworked}
          onChange={(e) => set({ unworked: e.target.checked })}
          style={{ accentColor: 'var(--accent)' }}
        />
        Необработавшие
      </label>
      {hasActive && (
        <button className="btn-ghost" style={{ fontSize: 12.5 }} onClick={() => onChange(EMPTY_FILTERS)}>
          Сбросить
        </button>
      )}
    </>
  );
}
